import { prefersReducedMotion } from "./motion";
import type { Teardown } from "./registry";

export type TrackerOptions = {
	/** Positioning context for the tracker. May itself be a horizontal scroll container. */
	root: HTMLElement;
	tracker: HTMLElement;
	/** Matches every item the tracker can sit on. */
	item: string;
	/** Where the tracker returns to once the pointer and focus have both left `root`. */
	restTarget: () => HTMLElement | null;
	/** Added to `root` while the tracker is live, so CSS can drop the per-item resting style. */
	trackingClass: string;
	/** Attributes whose mutation inside `root` can move the rest target. */
	restAttributes?: readonly string[];
	/** Also takes `top` and `height` from the item. Off where CSS owns the indicator's own box. */
	followsHeight?: boolean;
	/** Fires when a target is committed, not when its animation lands, and again on every resize
	    with the same item. A consumer must be idempotent. */
	onSettle?: (item: HTMLElement | null) => void;
};

type Box = { x: number; y: number; width: number; height: number };

const DURATION = 300;

const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3;

const mix = (from: number, to: number, at: number) => from + (to - from) * at;

export const createTracker = ({
	root,
	tracker,
	item: itemSelector,
	restTarget,
	trackingClass,
	restAttributes = [],
	followsHeight = true,
	onSettle,
}: TrackerOptions): Teardown => {
	const listeners = new AbortController();
	let frame: number | undefined;
	let settled: HTMLElement | null = null;
	let hovered: HTMLElement | null = null;

	const resize = new ResizeObserver(() => {
		if (frame !== undefined) return;
		if (settled) moveTo(settled, false);
	});

	const itemFrom = (target: EventTarget | null): HTMLElement | null =>
		target instanceof Element ? target.closest<HTMLElement>(itemSelector) : null;

	const commit = (next: HTMLElement | null) => {
		settled = next;
		onSettle?.(next);
	};

	const boxOf = (element: HTMLElement): Box => {
		const rootBox = root.getBoundingClientRect();
		const box = element.getBoundingClientRect();

		return {
			x: box.left - rootBox.left + root.scrollLeft,
			y: box.top - rootBox.top + root.scrollTop,
			width: box.width,
			height: box.height,
		};
	};

	const paint = ({ x, width }: Box) => {
		tracker.style.width = `${String(width)}px`;
		tracker.style.translate = `${String(x)}px`;
	};

	const stop = () => {
		if (frame !== undefined) cancelAnimationFrame(frame);
		frame = undefined;
	};

	const moveTo = (item: HTMLElement | null, animated: boolean) => {
		stop();

		if (!item) {
			tracker.style.opacity = "0";
			commit(null);
			return;
		}

		if (followsHeight) {
			const box = boxOf(item);

			tracker.style.top = `${String(box.y)}px`;
			tracker.style.height = `${String(box.height)}px`;
		}

		/* From nowhere, the tracker appears in place rather than flying in from the origin. */
		const cold = settled === null;

		if (cold || !animated || prefersReducedMotion()) {
			paint(boxOf(item));
			tracker.style.opacity = "1";
			commit(item);
			return;
		}

		const from = boxOf(tracker);
		const started = performance.now();

		tracker.style.opacity = "1";

		/* Re-measured each frame, or a target read once lands the pill short of a growing item. */
		const step = (now: number) => {
			const at = easeOutCubic(Math.min(1, Math.max(0, (now - started) / DURATION)));
			const to = boxOf(item);

			paint({ ...to, x: mix(from.x, to.x, at), width: mix(from.width, to.width, at) });

			frame = at < 1 ? requestAnimationFrame(step) : undefined;
		};

		frame = requestAnimationFrame(step);
		commit(item);
	};

	/* The hovered item outranks the rest target, so a panel closing under the pointer cannot
	   pull the tracker out from under it. */
	const rest = () => {
		moveTo(hovered ?? restTarget(), true);
	};

	const options = { signal: listeners.signal };

	root.addEventListener(
		"pointerover",
		(event) => {
			hovered = itemFrom(event.target);
			if (hovered) moveTo(hovered, true);
		},
		options,
	);

	root.addEventListener(
		"focusin",
		(event) => {
			const item = itemFrom(event.target);
			if (item) moveTo(item, true);
		},
		options,
	);

	root.addEventListener(
		"pointerleave",
		() => {
			hovered = null;
			rest();
		},
		options,
	);

	/* `relatedTarget` outside the root means focus really left, not just moved between items. */
	root.addEventListener(
		"focusout",
		(event) => {
			const next = event.relatedTarget;
			if (!(next instanceof Node) || !root.contains(next)) rest();
		},
		options,
	);

	const expansion = restAttributes.length ? new MutationObserver(() => rest()) : undefined;

	expansion?.observe(root, { subtree: true, attributeFilter: [...restAttributes] });

	resize.observe(root);
	root.classList.add(trackingClass);
	moveTo(restTarget(), false);

	return () => {
		listeners.abort();
		expansion?.disconnect();
		resize.disconnect();
		stop();
		root.classList.remove(trackingClass);

		for (const property of ["top", "height", "width", "translate", "opacity"]) {
			tracker.style.removeProperty(property);
		}
	};
};
